/**
 * Graph Traversal Panel Component
 *
 * DC-698: Displays graph traversal efficiency metrics for the Knowledge Graph.
 * Shows APL, diameter, reachability, and interoperability lift over SQL joins.
 *
 * @see Documentation/Development/Design Docs/Missing_Accordion_Capabilities_Design.md
 */

import React, { useState, useEffect } from 'react';
import './graph-traversal-panel.css';

// Types matching server/semantic/graphTraversalMetrics.ts
interface TraversalMetrics {
  averagePathLength: number;
  diameter: number;
  reachabilityScore: number;
  disconnectedComponents: number;
  hopDistribution: Record<number, number>;
  totalNodes: number;
  totalEdges: number;
  sampleSize: number;
  analyzedAt: string;
}

interface PathAnalysis {
  source: string;
  target: string;
  hops: number;
  path: string[];
  predicates: string[];
}

interface RelationshipComplexity {
  predicateType: string;
  displayName: string;
  count: number;
  joinsPerHop: number;
  rationale: string;
}

interface InteroperabilityLift {
  averageGraphHops: number;
  estimatedSqlJoins: number;
  liftFactor: number;
  effectiveLiftFactor?: number;
  reachabilityPercent?: number;
  reachabilityLimited?: boolean;
  interpretation: string;
  relationshipBreakdown?: RelationshipComplexity[];
  weightedJoinsPerHop?: number;
  totalRelationships?: number;
}

interface StructuredRecommendation {
  severity: 'high' | 'medium' | 'low';
  issue: string;
  impact: string;
  action: string;
  expectedOutcome: string;
  showMeType?: 'clusters' | 'disconnected-pairs' | 'connected-pairs' | 'bridges';
}

interface ClusterConcept {
  id: string;
  label: string;
  definition?: string;
}

interface ClusterInfo {
  clusterId: number;
  size: number;
  concepts: ClusterConcept[];
}

interface ConceptPair {
  source: ClusterConcept;
  target: ClusterConcept;
  hops?: number;
  path?: string[];
}

interface BridgeSuggestion {
  fromConcept: ClusterConcept;
  toConcept: ClusterConcept;
  fromClusterId: number;
  toClusterId: number;
  reason: string;
  similarityScore: number;
}

interface ClusterAnalysis {
  clusters: ClusterInfo[];
  sampleDisconnectedPairs: ConceptPair[];
  sampleConnectedPairs: ConceptPair[];
  bridgeSuggestions: BridgeSuggestion[];
}

interface GraphTraversalReport {
  analyzedAt: string;
  metrics: TraversalMetrics;
  interoperabilityLift: InteroperabilityLift;
  samplePaths: PathAnalysis[];
  recommendations: string[];
  structuredRecommendations?: StructuredRecommendation[];
  clusterAnalysis?: ClusterAnalysis;
}

interface GraphTraversalPanelProps {
  /** Journey ID - use this when showing graph traversal for a journey's ontology station */
  journeyId?: string;
  /** Ontology ID - use this when showing graph traversal for a private ontology directly */
  ontologyId?: string;
  onAnalysisComplete?: (report: GraphTraversalReport) => void;
}

type ShowMeType = 'clusters' | 'disconnected-pairs' | 'connected-pairs' | 'bridges';

// Default fallback if no breakdown available
const DEFAULT_JOINS_PER_HOP = 3;

const GraphTraversalPanel: React.FC<GraphTraversalPanelProps> = ({
  journeyId,
  ontologyId,
  onAnalysisComplete
}) => {
  const [report, setReport] = useState<GraphTraversalReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedPath, setExpandedPath] = useState<number | null>(null);
  const [activeShowMe, setActiveShowMe] = useState<ShowMeType | null>(null);
  const [showMethodology, setShowMethodology] = useState(false);

  // Determine which ID to use
  const targetId = ontologyId || journeyId;
  const isOntologyMode = !!ontologyId;

  useEffect(() => {
    if (targetId) {
      fetchGraphTraversal();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchGraphTraversal is defined in component
  }, [targetId]);

  const fetchGraphTraversal = async () => {
    if (!targetId) {
      setError('No journey or ontology ID provided');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Use different endpoint based on mode
      const endpoint = isOntologyMode
        ? `/api/graph-traversal/ontology/${targetId}`
        : `/api/graph-traversal/${targetId}`;

      const response = await fetch(endpoint);

      if (!response.ok) {
        if (response.status === 404) {
          setError('No ontology graph found. Run Ontology Inference station first.');
          return;
        }
        throw new Error(`Failed to fetch graph traversal: ${response.statusText}`);
      }

      const data = await response.json();

      // Handle both response formats:
      // - Journey endpoint returns { success, journeyId, report, graphSummary }
      // - Ontology endpoint returns { success, ontologyId, report, graphSummary }
      // We need to extract the 'report' property which contains metrics, interoperabilityLift, etc.
      const reportData = data.report || data;
      setReport(reportData);
      onAnalysisComplete?.(reportData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load graph traversal data');
    } finally {
      setLoading(false);
    }
  };

  const getConnectivityBadge = (reachability: number): { label: string; color: string } => {
    if (reachability >= 95) return { label: 'Excellent', color: '#16a34a' };
    if (reachability >= 80) return { label: 'Good', color: '#2563eb' };
    if (reachability >= 60) return { label: 'Moderate', color: '#d97706' };
    return { label: 'Poor', color: '#dc2626' };
  };

  const getLiftBadge = (lift: InteroperabilityLift): { label: string; color: string; isLimited: boolean } => {
    // Use effective lift factor if available, which accounts for reachability
    const effectiveLift = lift.effectiveLiftFactor ?? lift.liftFactor;
    const isLimited = lift.reachabilityLimited ?? false;

    if (isLimited) {
      // High raw efficiency but limited by poor reachability
      return { label: 'Limited by Connectivity', color: '#d97706', isLimited: true };
    }

    if (effectiveLift >= 3) return { label: 'High Efficiency', color: '#16a34a', isLimited: false };
    if (effectiveLift >= 2) return { label: 'Good Efficiency', color: '#2563eb', isLimited: false };
    if (effectiveLift >= 1.5) return { label: 'Moderate Efficiency', color: '#d97706', isLimited: false };
    return { label: 'Low Efficiency', color: '#dc2626', isLimited: false };
  };

  // Generate user-friendly interpretations for metrics
  const getAplInterpretation = (apl: number): { summary: string; detail: string } => {
    if (apl <= 2) {
      return {
        summary: 'Excellent navigability',
        detail: 'Most concepts are closely related. Users can discover connections quickly without traversing many intermediate nodes.'
      };
    }
    if (apl <= 3) {
      return {
        summary: 'Good navigability',
        detail: 'Concepts are reasonably well-connected. Users can typically find relationships within a few logical steps.'
      };
    }
    if (apl <= 4) {
      return {
        summary: 'Moderate navigability',
        detail: 'Some concepts require multiple hops to connect. Consider adding direct relationships for frequently queried concept pairs.'
      };
    }
    return {
      summary: 'Navigation challenges',
      detail: 'Concepts are sparsely connected. Users may struggle to discover relationships. Consider adding bridging concepts or direct links.'
    };
  };

  const getDiameterInterpretation = (diameter: number, totalNodes: number): { summary: string; detail: string } => {
    const ratio = totalNodes > 0 ? diameter / Math.log2(totalNodes) : 0;
    if (diameter <= 3) {
      return {
        summary: 'Compact structure',
        detail: 'Even the most distant concepts are only a few steps apart. This makes the ontology easy to explore end-to-end.'
      };
    }
    if (diameter <= 6 || ratio <= 2) {
      return {
        summary: 'Well-structured',
        detail: 'The longest path is reasonable for an ontology of this size. Most queries will complete efficiently.'
      };
    }
    if (diameter <= 10) {
      return {
        summary: 'Extended structure',
        detail: 'Some concept pairs are far apart. Consider whether hub concepts or shortcuts could reduce traversal distances.'
      };
    }
    return {
      summary: 'Sparse structure',
      detail: 'The graph has long chains with few shortcuts. This may indicate missing cross-domain relationships.'
    };
  };

  const getHopDistributionInsight = (distribution: Record<number, number>): { summary: string; detail: string } => {
    const entries = Object.entries(distribution).map(([k, v]) => ({ hops: Number(k), count: v }));
    const total = entries.reduce((sum, e) => sum + e.count, 0);
    const shortHops = entries.filter(e => e.hops <= 2).reduce((sum, e) => sum + e.count, 0);
    const longHops = entries.filter(e => e.hops >= 5).reduce((sum, e) => sum + e.count, 0);
    const shortPercent = total > 0 ? Math.round((shortHops / total) * 100) : 0;
    const longPercent = total > 0 ? Math.round((longHops / total) * 100) : 0;

    if (shortPercent >= 70) {
      return {
        summary: `${shortPercent}% of queries resolve in 1-2 hops`,
        detail: 'Most relationships are direct or nearly direct. This is ideal for fast query performance and intuitive navigation.'
      };
    }
    if (shortPercent >= 50) {
      return {
        summary: `${shortPercent}% short paths, ${longPercent}% long paths`,
        detail: 'A good mix of direct and indirect relationships. Users can find common connections quickly while still discovering deeper relationships.'
      };
    }
    if (longPercent >= 40) {
      return {
        summary: `${longPercent}% of queries require 5+ hops`,
        detail: 'Many relationships require traversing through multiple intermediate concepts. Consider adding direct links for frequently accessed concept pairs.'
      };
    }
    return {
      summary: 'Moderate path length distribution',
      detail: 'Path lengths are spread across the range. Review which concept pairs are frequently queried together and consider optimizing those paths.'
    };
  };

  const getLiftInterpretation = (lift: InteroperabilityLift): { summary: string; detail: string; example: string; warning?: string } => {
    const { liftFactor, effectiveLiftFactor, reachabilityPercent, reachabilityLimited, averageGraphHops, estimatedSqlJoins } = lift;

    // Use effective lift for assessment
    const effectiveLift = effectiveLiftFactor ?? liftFactor;
    const reachability = reachabilityPercent ?? 100;

    let summary: string;
    let detail: string;
    let warning: string | undefined;

    // Check if reachability is limiting the efficiency
    if (reachabilityLimited || (reachability < 50 && liftFactor >= 2)) {
      summary = 'Efficiency limited by connectivity gaps';
      detail = `For the ${reachability}% of concept pairs that CAN reach each other, traversal is ${liftFactor}x more efficient than SQL. However, ${100 - reachability}% of pairs have no connection at all.`;
      warning = `Effective efficiency across ALL pairs: ${effectiveLift}x. Priority should be connecting isolated clusters before optimizing traversal speed.`;
    } else if (reachability < 80 && liftFactor >= 2) {
      summary = 'Good efficiency with connectivity gaps';
      detail = `Connected pairs traverse efficiently (${liftFactor}x), but ${100 - reachability}% of concept pairs remain disconnected.`;
      warning = `Effective efficiency: ${effectiveLift}x when accounting for unreachable pairs.`;
    } else if (effectiveLift >= 4) {
      summary = 'Exceptional interoperability advantage';
      detail = 'The knowledge graph dramatically simplifies data access compared to traditional relational queries.';
    } else if (effectiveLift >= 3) {
      summary = 'Strong interoperability advantage';
      detail = 'The graph structure significantly reduces complexity for cross-domain queries.';
    } else if (effectiveLift >= 2) {
      summary = 'Good interoperability advantage';
      detail = 'The knowledge graph provides meaningful simplification over SQL-based approaches.';
    } else if (effectiveLift >= 1.5) {
      summary = 'Moderate advantage';
      detail = 'Some benefit from graph structure, but there may be opportunities to improve connectivity.';
    } else {
      summary = 'Limited advantage';
      detail = 'The graph structure provides minimal benefit over relational queries. Consider enriching relationships.';
    }

    // Generate a practical example
    const example = `A query that would require ${Math.round(estimatedSqlJoins)} table joins in SQL can be answered with just ${averageGraphHops.toFixed(1)} graph traversals.`;

    return { summary, detail, example, warning };
  };

  const renderHopDistribution = (distribution: Record<number, number>, insight: { summary: string; detail: string }) => {
    const maxCount = Math.max(...Object.values(distribution), 1);
    const entries = Object.entries(distribution).sort((a, b) => Number(a[0]) - Number(b[0]));
    const total = Object.values(distribution).reduce((sum, v) => sum + v, 0);

    return (
      <div className="hop-distribution">
        <div className="hop-header">
          <h4>Hop Distribution</h4>
          <span className="hop-total">{total} paths sampled</span>
        </div>
        <div className="hop-insight">
          <span className="hop-insight-summary">{insight.summary}</span>
          <span className="hop-insight-detail">{insight.detail}</span>
        </div>
        <div className="distribution-bars">
          {entries.map(([hops, count]) => {
            const percent = total > 0 ? Math.round((count / total) * 100) : 0;
            const hopNum = Number(hops);
            return (
              <div key={hops} className="bar-container">
                <div className="bar-label">{hops} hop{hopNum !== 1 ? 's' : ''}</div>
                <div className="bar-wrapper">
                  <div
                    className="bar"
                    style={{
                      width: `${(count / maxCount) * 100}%`,
                      backgroundColor: hopNum <= 2 ? '#16a34a' : hopNum <= 4 ? '#2563eb' : '#d97706'
                    }}
                  />
                </div>
                <div className="bar-count">{count} <span className="bar-percent">({percent}%)</span></div>
              </div>
            );
          })}
        </div>
        <div className="hop-legend">
          <span className="legend-item"><span className="legend-dot" style={{ background: '#16a34a' }}></span> 1-2 hops (fast)</span>
          <span className="legend-item"><span className="legend-dot" style={{ background: '#2563eb' }}></span> 3-4 hops (moderate)</span>
          <span className="legend-item"><span className="legend-dot" style={{ background: '#d97706' }}></span> 5+ hops (slow)</span>
        </div>
      </div>
    );
  };

  const renderSamplePath = (path: PathAnalysis, index: number) => {
    const isExpanded = expandedPath === index;

    return (
      <div key={index} className="sample-path">
        <div
          className="path-header"
          onClick={() => setExpandedPath(isExpanded ? null : index)}
        >
          <span className="path-summary">
            <span className="node-label">{formatNodeId(path.source)}</span>
            <span className="path-arrow">→</span>
            <span className="node-label">{formatNodeId(path.target)}</span>
          </span>
          <span className="hop-badge">{path.hops} hop{path.hops !== 1 ? 's' : ''}</span>
          <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>▶</span>
        </div>
        {isExpanded && (
          <div className="path-details">
            <div className="path-visualization">
              {path.path.map((node, i) => (
                <React.Fragment key={i}>
                  <span className="path-node">{formatNodeId(node)}</span>
                  {i < path.predicates.length && (
                    <span className="path-edge">
                      <span className="edge-line">—</span>
                      <span className="edge-label">{formatPredicate(path.predicates[i])}</span>
                      <span className="edge-line">→</span>
                    </span>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const formatNodeId = (id: string): string => {
    // Extract last part of URI or return as-is
    const parts = id.split(/[#/]/);
    return parts[parts.length - 1] || id;
  };

  const formatPredicate = (predicate: string): string => {
    const parts = predicate.split(/[#/]/);
    return parts[parts.length - 1] || predicate;
  };

  const getShowMeTitle = (type: ShowMeType): string => {
    switch (type) {
      case 'clusters': return 'Cluster Breakdown';
      case 'disconnected-pairs': return 'Disconnected Concept Pairs';
      case 'connected-pairs': return 'Connected Concept Pairs';
      case 'bridges': return 'Suggested Bridge Connections';
    }
  };

  const renderShowMeContent = (type: ShowMeType) => {
    const analysis = report?.clusterAnalysis;
    if (!analysis) {
      return <p className="show-me-empty">No cluster analysis data available.</p>;
    }

    switch (type) {
      case 'clusters':
        return (
          <div className="show-me-clusters">
            <p className="show-me-description">
              Your ontology has {analysis.clusters.length} disconnected cluster{analysis.clusters.length !== 1 ? 's' : ''}.
              Concepts within each cluster can reach each other, but cannot reach concepts in other clusters.
            </p>
            <div className="cluster-list">
              {analysis.clusters.map((cluster) => (
                <div key={cluster.clusterId} className="cluster-item">
                  <div className="cluster-header">
                    <span className="cluster-name">Cluster {cluster.clusterId}</span>
                    <span className="cluster-size">{cluster.size} concept{cluster.size !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="cluster-concepts">
                    {cluster.concepts.slice(0, 8).map((concept) => (
                      <div key={concept.id} className="concept-chip" title={concept.definition || concept.id}>
                        <span className="concept-label">{formatNodeId(concept.label || concept.id)}</span>
                        {concept.definition && (
                          <span className="concept-definition">{concept.definition.slice(0, 60)}{concept.definition.length > 60 ? '...' : ''}</span>
                        )}
                      </div>
                    ))}
                    {cluster.concepts.length > 8 && (
                      <div className="concept-chip more">+{cluster.concepts.length - 8} more</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'disconnected-pairs':
        return (
          <div className="show-me-pairs">
            <p className="show-me-description">
              These concept pairs have no path between them in the ontology.
              Adding relationships between these concepts would improve graph connectivity.
            </p>
            {analysis.sampleDisconnectedPairs.length === 0 ? (
              <p className="show-me-empty">No disconnected pairs found - your ontology is fully connected!</p>
            ) : (
              <div className="pair-list">
                {analysis.sampleDisconnectedPairs.map((pair, i) => (
                  <div key={i} className="pair-item disconnected">
                    <div className="pair-concepts">
                      <div className="pair-concept">
                        <span className="concept-label">{formatNodeId(pair.source.label || pair.source.id)}</span>
                        {pair.source.definition && (
                          <span className="concept-definition">{pair.source.definition}</span>
                        )}
                      </div>
                      <span className="pair-separator">⊗</span>
                      <div className="pair-concept">
                        <span className="concept-label">{formatNodeId(pair.target.label || pair.target.id)}</span>
                        {pair.target.definition && (
                          <span className="concept-definition">{pair.target.definition}</span>
                        )}
                      </div>
                    </div>
                    <span className="pair-status no-path">No path exists</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 'connected-pairs':
        return (
          <div className="show-me-pairs">
            <p className="show-me-description">
              These concept pairs are connected through the ontology.
              The hop count shows how many relationships you need to traverse to get from one to the other.
            </p>
            {analysis.sampleConnectedPairs.length === 0 ? (
              <p className="show-me-empty">No connected pairs found in sample.</p>
            ) : (
              <div className="pair-list">
                {analysis.sampleConnectedPairs.map((pair, i) => (
                  <div key={i} className="pair-item connected">
                    <div className="pair-concepts">
                      <div className="pair-concept">
                        <span className="concept-label">{formatNodeId(pair.source.label || pair.source.id)}</span>
                        {pair.source.definition && (
                          <span className="concept-definition">{pair.source.definition}</span>
                        )}
                      </div>
                      <span className="pair-separator">→</span>
                      <div className="pair-concept">
                        <span className="concept-label">{formatNodeId(pair.target.label || pair.target.id)}</span>
                        {pair.target.definition && (
                          <span className="concept-definition">{pair.target.definition}</span>
                        )}
                      </div>
                    </div>
                    <span className="pair-status has-path">{pair.hops} hop{pair.hops !== 1 ? 's' : ''}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 'bridges':
        return (
          <div className="show-me-bridges">
            <p className="show-me-description">
              These are suggested connections that could bridge disconnected clusters based on definition similarity.
              Adding relationships between these concept pairs would significantly improve graph connectivity.
            </p>
            {analysis.bridgeSuggestions.length === 0 ? (
              <p className="show-me-empty">No bridge suggestions available.</p>
            ) : (
              <div className="bridge-list">
                {analysis.bridgeSuggestions.map((bridge, i) => (
                  <div key={i} className="bridge-item">
                    <div className="bridge-header">
                      <span className="bridge-clusters">
                        Cluster {bridge.fromClusterId} → Cluster {bridge.toClusterId}
                      </span>
                      <span className="bridge-score" title="Definition similarity score">
                        {Math.round(bridge.similarityScore * 100)}% similar
                      </span>
                    </div>
                    <div className="bridge-concepts">
                      <div className="bridge-concept from">
                        <span className="concept-label">{formatNodeId(bridge.fromConcept.label || bridge.fromConcept.id)}</span>
                        {bridge.fromConcept.definition && (
                          <span className="concept-definition">{bridge.fromConcept.definition}</span>
                        )}
                      </div>
                      <span className="bridge-arrow">⟷</span>
                      <div className="bridge-concept to">
                        <span className="concept-label">{formatNodeId(bridge.toConcept.label || bridge.toConcept.id)}</span>
                        {bridge.toConcept.definition && (
                          <span className="concept-definition">{bridge.toConcept.definition}</span>
                        )}
                      </div>
                    </div>
                    <div className="bridge-reason">
                      <span className="reason-label">Why:</span> {bridge.reason}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="graph-traversal-panel loading">
        <div className="loading-spinner" />
        <p>Analyzing graph connectivity...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="graph-traversal-panel error">
        <div className="error-icon">⚠️</div>
        <p>{error}</p>
        <button onClick={fetchGraphTraversal} className="retry-button">
          Retry Analysis
        </button>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="graph-traversal-panel empty">
        <p>No graph traversal data available.</p>
      </div>
    );
  }

  const { metrics, interoperabilityLift, samplePaths, recommendations } = report;
  const connectivityBadge = getConnectivityBadge(metrics.reachabilityScore);
  const liftBadge = getLiftBadge(interoperabilityLift);

  // Get user-friendly interpretations
  const aplInsight = getAplInterpretation(metrics.averagePathLength);
  const diameterInsight = getDiameterInterpretation(metrics.diameter, metrics.totalNodes);
  const hopInsight = getHopDistributionInsight(metrics.hopDistribution);
  const liftInsight = getLiftInterpretation(interoperabilityLift);

  return (
    <div className="graph-traversal-panel">
      <div className="panel-header">
        <h3>Knowledge Graph Traversal Efficiency</h3>
        <span className="analysis-time">
          Analyzed {new Date(report.analyzedAt).toLocaleString()}
        </span>
      </div>

      {/* Metrics with Insights */}
      <div className="metrics-insights-section">
        <div className="metrics-grid">
          {/* Average Path Length */}
          <div className="metric-card">
            <div className="metric-value">{metrics.averagePathLength.toFixed(2)}</div>
            <div className="metric-label">Average Path Length</div>
            <div className="metric-sublabel">hops between concepts</div>
          </div>

          {/* Diameter */}
          <div className="metric-card">
            <div className="metric-value">{metrics.diameter}</div>
            <div className="metric-label">Graph Diameter</div>
            <div className="metric-sublabel">longest shortest path</div>
          </div>

          {/* Reachability */}
          <div className="metric-card">
            <div className="metric-value">
              {metrics.reachabilityScore}%
              <span
                className="status-badge"
                style={{ backgroundColor: connectivityBadge.color }}
              >
                {connectivityBadge.label}
              </span>
            </div>
            <div className="metric-label">Reachability Score</div>
            <div className="metric-sublabel">concept pairs connected</div>
          </div>

          {/* Components */}
          <div className="metric-card">
            <div className="metric-value">{metrics.disconnectedComponents}</div>
            <div className="metric-label">Connected Components</div>
            <div className="metric-sublabel">{metrics.disconnectedComponents === 1 ? 'fully connected' : 'disconnected clusters'}</div>
          </div>
        </div>

        {/* Metric Insights */}
        <div className="metric-insights">
          <div className="insight-card">
            <div className="insight-icon">🧭</div>
            <div className="insight-content">
              <div className="insight-summary">{aplInsight.summary}</div>
              <div className="insight-detail">{aplInsight.detail}</div>
            </div>
          </div>
          <div className="insight-card">
            <div className="insight-icon">📐</div>
            <div className="insight-content">
              <div className="insight-summary">{diameterInsight.summary}</div>
              <div className="insight-detail">{diameterInsight.detail}</div>
            </div>
          </div>
          {metrics.disconnectedComponents > 1 && (
            <div className="insight-card warning">
              <div className="insight-icon">⚠️</div>
              <div className="insight-content">
                <div className="insight-summary">{metrics.disconnectedComponents} isolated clusters detected</div>
                <div className="insight-detail">
                  Concepts in different clusters cannot reach each other. This limits cross-domain queries and knowledge discovery.
                  Consider adding bridging relationships between clusters.
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Interoperability Lift */}
      <div className={`lift-section ${liftBadge.isLimited ? 'reachability-limited' : ''}`}>
        <div className="lift-header">
          <h4>Interoperability Lift vs SQL</h4>
          <span className="lift-summary-badge" style={{ backgroundColor: liftBadge.color }}>
            {liftInsight.summary}
          </span>
        </div>
        <div className="lift-comparison">
          <div className="lift-item">
            <div className="lift-value">{interoperabilityLift.averageGraphHops.toFixed(1)}</div>
            <div className="lift-label">Graph Hops</div>
          </div>
          <div className="lift-vs">vs</div>
          <div className="lift-item">
            <div className="lift-value">{interoperabilityLift.estimatedSqlJoins.toFixed(0)}</div>
            <div className="lift-label">Estimated SQL Joins</div>
          </div>
          <div className="lift-factor">
            {interoperabilityLift.reachabilityLimited ? (
              <>
                <div className="lift-factor-dual">
                  <span className="factor-badge raw" title="For connected pairs only">
                    {interoperabilityLift.liftFactor}x
                  </span>
                  <span className="factor-arrow">→</span>
                  <span
                    className="factor-badge effective"
                    style={{ backgroundColor: liftBadge.color }}
                    title="Adjusted for reachability"
                  >
                    {interoperabilityLift.effectiveLiftFactor}x
                  </span>
                </div>
                <span className="factor-label-dual">
                  <span className="label-raw">Connected Pairs</span>
                  <span className="label-effective">Effective</span>
                </span>
              </>
            ) : (
              <>
                <span
                  className="factor-badge"
                  style={{ backgroundColor: liftBadge.color }}
                >
                  {interoperabilityLift.effectiveLiftFactor ?? interoperabilityLift.liftFactor}x
                </span>
                <span className="factor-label">{liftBadge.label}</span>
              </>
            )}
          </div>
        </div>

        {/* Warning when reachability limits efficiency */}
        {liftInsight.warning && (
          <div className="lift-warning">
            <span className="lift-warning-icon">⚠️</span>
            <span className="lift-warning-text">{liftInsight.warning}</span>
          </div>
        )}

        <div className="lift-insights">
          <div className="lift-insight-row">
            <span className="lift-insight-icon">💡</span>
            <span className="lift-insight-text">{liftInsight.detail}</span>
          </div>
          <div className="lift-insight-row example">
            <span className="lift-insight-icon">📊</span>
            <span className="lift-insight-text">{liftInsight.example}</span>
          </div>
        </div>

        {/* Methodology Explanation */}
        <div className="lift-methodology">
          <button
            className="methodology-toggle"
            onClick={() => setShowMethodology(!showMethodology)}
          >
            <span className="methodology-icon">{showMethodology ? '▼' : '▶'}</span>
            How is this calculated?
          </button>
          {showMethodology && (
            <div className="methodology-content">
              {/* Relationship Breakdown - specific to this ontology */}
              {interoperabilityLift.relationshipBreakdown && interoperabilityLift.relationshipBreakdown.length > 0 ? (
                <>
                  <div className="methodology-breakdown">
                    <div className="breakdown-title">
                      Relationship Types in This Ontology
                      <span className="breakdown-total">
                        {interoperabilityLift.totalRelationships} relationships analyzed
                      </span>
                    </div>
                    <div className="breakdown-table">
                      <div className="breakdown-header">
                        <span className="breakdown-col type">Type</span>
                        <span className="breakdown-col count">Count</span>
                        <span className="breakdown-col joins">SQL Joins/Hop</span>
                        <span className="breakdown-col rationale">Why</span>
                      </div>
                      {interoperabilityLift.relationshipBreakdown.map((rel, i) => {
                        const percent = interoperabilityLift.totalRelationships
                          ? Math.round((rel.count / interoperabilityLift.totalRelationships) * 100)
                          : 0;
                        return (
                          <div key={i} className="breakdown-row">
                            <span className="breakdown-col type">
                              <span className="rel-type-badge">{rel.displayName}</span>
                            </span>
                            <span className="breakdown-col count">
                              {rel.count} <span className="count-percent">({percent}%)</span>
                            </span>
                            <span className="breakdown-col joins">
                              <span
                                className="joins-badge"
                                style={{
                                  backgroundColor: rel.joinsPerHop <= 2 ? '#16a34a' :
                                    rel.joinsPerHop <= 3 ? '#2563eb' :
                                    rel.joinsPerHop <= 4 ? '#d97706' : '#dc2626'
                                }}
                              >
                                {rel.joinsPerHop}
                              </span>
                            </span>
                            <span className="breakdown-col rationale">{rel.rationale}</span>
                          </div>
                        );
                      })}
                    </div>
                    <div className="breakdown-summary">
                      <span className="summary-label">Weighted Average:</span>
                      <span className="summary-value">
                        {interoperabilityLift.weightedJoinsPerHop?.toFixed(2)} joins per hop
                      </span>
                      <span className="summary-note">
                        (based on your ontology's actual relationship distribution)
                      </span>
                    </div>
                  </div>

                  <div className="methodology-formula">
                    <div className="formula-title">Your Ontology's Calculation</div>
                    <div className="formula-equation">
                      <span className="formula-var">Estimated SQL Joins</span>
                      <span className="formula-equals">=</span>
                      <span className="formula-var">Graph Hops</span>
                      <span className="formula-times">×</span>
                      <span className="formula-const">{interoperabilityLift.weightedJoinsPerHop?.toFixed(2)}</span>
                    </div>
                    <div className="formula-example">
                      {interoperabilityLift.averageGraphHops.toFixed(1)} hops × {interoperabilityLift.weightedJoinsPerHop?.toFixed(2)} = {interoperabilityLift.estimatedSqlJoins.toFixed(1)} estimated joins
                    </div>
                  </div>
                </>
              ) : (
                <div className="methodology-formula">
                  <div className="formula-title">Estimation Formula</div>
                  <div className="formula-equation">
                    <span className="formula-var">Estimated SQL Joins</span>
                    <span className="formula-equals">=</span>
                    <span className="formula-var">Graph Hops</span>
                    <span className="formula-times">×</span>
                    <span className="formula-const">{DEFAULT_JOINS_PER_HOP}</span>
                  </div>
                  <div className="formula-example">
                    {interoperabilityLift.averageGraphHops.toFixed(1)} hops × {DEFAULT_JOINS_PER_HOP} = {interoperabilityLift.estimatedSqlJoins.toFixed(0)} estimated joins
                  </div>
                </div>
              )}

              <div className="methodology-rationale">
                <div className="rationale-title">How relationship types map to SQL complexity</div>
                <div className="rationale-content">
                  <p>Different semantic relationships require different SQL patterns when stored relationally:</p>
                  <ul>
                    <li><strong>Equivalence</strong> (owl:sameAs) → 1 join – Simple alias lookup</li>
                    <li><strong>Hierarchical</strong> (rdfs:subClassOf) → 2 joins – Parent + type tables</li>
                    <li><strong>Associative</strong> (relatedTo, hasPart) → 3 joins – Junction table pattern</li>
                    <li><strong>Causal/Process</strong> (causes, triggers) → 4 joins – State + transition tables</li>
                    <li><strong>Compliance</strong> (validates, audits) → 5 joins – Evidence + audit trail</li>
                  </ul>
                </div>
              </div>

              <div className="methodology-scale">
                <div className="scale-title">Efficiency Scale</div>
                <div className="scale-items">
                  <div className="scale-item">
                    <span className="scale-badge" style={{ backgroundColor: '#16a34a' }}>≥3x</span>
                    <span className="scale-label">High Efficiency</span>
                    <span className="scale-desc">Excellent graph structure, significant query simplification</span>
                  </div>
                  <div className="scale-item">
                    <span className="scale-badge" style={{ backgroundColor: '#2563eb' }}>2-3x</span>
                    <span className="scale-label">Good Efficiency</span>
                    <span className="scale-desc">Well-connected graph with meaningful lift</span>
                  </div>
                  <div className="scale-item">
                    <span className="scale-badge" style={{ backgroundColor: '#d97706' }}>1.5-2x</span>
                    <span className="scale-label">Moderate Efficiency</span>
                    <span className="scale-desc">Some benefit, opportunities for improvement</span>
                  </div>
                  <div className="scale-item">
                    <span className="scale-badge" style={{ backgroundColor: '#dc2626' }}>&lt;1.5x</span>
                    <span className="scale-label">Low Efficiency</span>
                    <span className="scale-desc">Sparse connectivity, consider enriching relationships</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Hop Distribution */}
      {Object.keys(metrics.hopDistribution).length > 0 &&
        renderHopDistribution(metrics.hopDistribution, hopInsight)
      }

      {/* Sample Paths */}
      {samplePaths.length > 0 && (
        <div className="sample-paths-section">
          <h4>Sample Traversal Paths</h4>
          <div className="sample-paths">
            {samplePaths.map((path, i) => renderSamplePath(path, i))}
          </div>
        </div>
      )}

      {/* Structured Recommendations */}
      {report.structuredRecommendations && report.structuredRecommendations.length > 0 && (
        <div className="recommendations-section">
          <h4>Recommendations</h4>
          <div className="structured-recommendations">
            {report.structuredRecommendations.map((rec, i) => (
              <div key={i} className={`recommendation-card severity-${rec.severity}`}>
                <div className="rec-header">
                  <span className={`severity-badge ${rec.severity}`}>
                    {rec.severity === 'high' ? '!' : rec.severity === 'medium' ? '•' : '○'}
                  </span>
                  <span className="rec-issue">{rec.issue}</span>
                </div>
                <div className="rec-body">
                  <div className="rec-row">
                    <span className="rec-label">Impact</span>
                    <span className="rec-content">{rec.impact}</span>
                  </div>
                  <div className="rec-row">
                    <span className="rec-label">Action</span>
                    <span className="rec-content">{rec.action}</span>
                  </div>
                  <div className="rec-row">
                    <span className="rec-label">Outcome</span>
                    <span className="rec-content">{rec.expectedOutcome}</span>
                  </div>
                  {rec.showMeType && report.clusterAnalysis && (
                    <div className="rec-row rec-show-me">
                      <button
                        className="show-me-button"
                        onClick={() => setActiveShowMe(rec.showMeType || null)}
                      >
                        Show Me
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Show Me Modal */}
      {activeShowMe && (
        <div className="show-me-overlay" onClick={() => setActiveShowMe(null)}>
          <div className="show-me-modal" onClick={(e) => e.stopPropagation()}>
            <div className="show-me-header">
              <h4>{getShowMeTitle(activeShowMe)}</h4>
              <button className="show-me-close" onClick={() => setActiveShowMe(null)}>×</button>
            </div>
            <div className="show-me-body">
              {renderShowMeContent(activeShowMe)}
            </div>
          </div>
        </div>
      )}

      {/* Legacy Recommendations (fallback) */}
      {(!report.structuredRecommendations || report.structuredRecommendations.length === 0) && recommendations.length > 0 && (
        <div className="recommendations-section">
          <h4>Recommendations</h4>
          <ul className="recommendations-list">
            {recommendations.map((rec, i) => (
              <li key={i}>{rec}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Graph Stats Footer */}
      <div className="graph-stats">
        <span>{metrics.totalNodes} nodes</span>
        <span className="separator">|</span>
        <span>{metrics.totalEdges} edges</span>
        <span className="separator">|</span>
        <span>{metrics.sampleSize} pairs sampled</span>
      </div>
    </div>
  );
};

export default GraphTraversalPanel;
